import gql from 'graphql-tag';
import apolloClient from '../apollo';

// State
const getDefaultTimeTracking = () => {
  return {
    user: null,
    project: { customer: null },
    appointment: { project: { customer: null } },
    startTime: null,
    stopTime: null,
    timeTrackingState: 'STARTED',
    timeTrackingType: 'WORKING_TIME',
    timeTrackingTotalHours: null,
    number: '_new',
  };
};

const getDefaultState = () => {
  return {
    timeTrackings: [],
    timeTrackingCount: 0,
  };
};

const state = getDefaultState();

// Request Fields
const TIME_TRACKING_REQUEST_FIELDS = gql`
  fragment timeTrackingFields on TimeTracking {
    id
    number
    startTime
    stopTime
    timeTrackingState
    timeTrackingType
    createdAt
    duration
    user {
      firstname
      lastname
      company
    }
    project {
      id
      number
      customer {
        firstname
        lastname
        zip
        street
        streetNumber
        streetNumberSuffix
        city
      }
    }
    appointment {
      id
      number
      installationStartAt
      installationEndAt
      appointmentType
      appointmentState
      appointmentApprovedState
      description
      project {
        id
        number
        customer {
          firstname
          lastname
          zip
          city
        }
      }
    }
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api for time tracking
 * @param {*} timeTracking
 */
const createPayload = (timeTracking) => {
  const payload = {
    ...(timeTracking.project ? { project: timeTracking.project.id } : {}),
    ...(timeTracking.appointment ? { appointment: timeTracking.appointment.id } : {}),
    ...(timeTracking.startTime ? { startTime: timeTracking.startTime } : {}),
    ...(timeTracking.stopTime ? { stopTime: timeTracking.stopTime } : {}),
    ...(timeTracking.timeTrackingState
      ? { timeTrackingState: timeTracking.timeTrackingState }
      : {}),
    ...(timeTracking.timeTrackingType ? { timeTrackingType: timeTracking.timeTrackingType } : {}),
    ...(timeTracking.user ? { user: timeTracking.user.id } : {}),
  };
  // console.log('createPayload():', payload);

  return payload;
};

/** Payload of the block project-time-tracking: without project, the server sets it. */
const createProjectTimeTrackingPayload = (timeTracking) => {
  const { project, ...payload } = createPayload(timeTracking);
  return payload;
};

const TIME_TRACKING_LIST_QUERY = gql`
  query timeTrackingsPaginated(
    $page: Int!
    $pageSize: Int!
    $sortField: String
    $sortOrder: Int
    $filters: TimeTrackingFilterInput
  ) {
    timeTrackingsPaginated(
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortOrder: $sortOrder
      filters: $filters
    ) {
      timeTrackingCount
      timeTrackingTotalHours
      timeTrackings {
        ...timeTrackingFields
      }
    }
  }
  ${TIME_TRACKING_REQUEST_FIELDS}
`;

/** Time tracking list of the block project-time-tracking; the server restricts it to the project. */
const PROJECT_TIME_TRACKING_LIST_QUERY = gql`
  query projectTimeTrackings(
    $projectNumber: Int!
    $page: Int!
    $pageSize: Int!
    $sortField: String
    $sortOrder: Int
    $filters: TimeTrackingFilterInput
  ) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        timeTracking {
          id
          number
          timeTrackings(
            page: $page
            pageSize: $pageSize
            sortField: $sortField
            sortOrder: $sortOrder
            filters: $filters
          ) {
            timeTrackingCount
            timeTrackingTotalHours
            timeTrackings {
              ...timeTrackingFields
            }
          }
        }
      }
    }
  }
  ${TIME_TRACKING_REQUEST_FIELDS}
`;

/** Appointments of the project for the appointment picker of the block project-time-tracking. */
const PROJECT_TIME_TRACKING_APPOINTMENT_OPTIONS_QUERY = gql`
  query projectTimeTrackingAppointmentOptions($projectNumber: Int!) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        timeTracking {
          id
          number
          appointmentOptions {
            id
            number
            installationStartAt
            installationEndAt
            appointmentType
            project {
              id
              number
              customer {
                firstname
                lastname
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Request of the list: with a project number the block project-time-tracking, without it the
 * global query timeTrackingsPaginated. `pagesOf` returns the `TimeTrackingPages` of the response.
 */
const timeTrackingListRequest = (projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_TIME_TRACKING_LIST_QUERY,
      pagesOf: (data) => data.projectByNumber.blocks.timeTracking.timeTrackings,
    };
  }
  return { query: TIME_TRACKING_LIST_QUERY, pagesOf: (data) => data.timeTrackingsPaginated };
};

/**
 * Request of a create: with a project number the mutation of the block project-time-tracking
 * (payload without project), without it the global mutation.
 */
const createTimeTrackingRequest = (timeTracking, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation createProjectTimeTracking(
          $projectNumber: Int!
          $payload: ProjectTimeTrackingInput!
        ) {
          createProjectTimeTracking(projectNumber: $projectNumber, timeTrackingInput: $payload) {
            ...timeTrackingFields
          }
        }
        ${TIME_TRACKING_REQUEST_FIELDS}
      `,
      variables: { projectNumber, payload: createProjectTimeTrackingPayload(timeTracking) },
      resultField: 'createProjectTimeTracking',
    };
  }
  return {
    mutation: gql`
      mutation createTimeTracking($payload: TimeTrackingInput!) {
        createTimeTracking(timeTrackingInput: $payload) {
          ...timeTrackingFields
        }
      }
      ${TIME_TRACKING_REQUEST_FIELDS}
    `,
    variables: { payload: createPayload(timeTracking) },
    resultField: 'createTimeTracking',
  };
};

/** Request of an update, chosen by the project number as `createTimeTrackingRequest`. */
const updateTimeTrackingRequest = (timeTracking, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation updateProjectTimeTracking(
          $projectNumber: Int!
          $timeTrackingNumber: Int!
          $payload: ProjectTimeTrackingInput!
        ) {
          updateProjectTimeTracking(
            projectNumber: $projectNumber
            timeTrackingNumber: $timeTrackingNumber
            timeTrackingInput: $payload
          ) {
            ...timeTrackingFields
          }
        }
        ${TIME_TRACKING_REQUEST_FIELDS}
      `,
      variables: {
        projectNumber,
        timeTrackingNumber: timeTracking.number,
        payload: createProjectTimeTrackingPayload(timeTracking),
      },
      resultField: 'updateProjectTimeTracking',
    };
  }
  return {
    mutation: gql`
      mutation updateTimeTracking($timeTrackingNumber: Int!, $payload: TimeTrackingInput!) {
        updateTimeTracking(timeTrackingNumber: $timeTrackingNumber, timeTrackingInput: $payload) {
          ...timeTrackingFields
        }
      }
      ${TIME_TRACKING_REQUEST_FIELDS}
    `,
    variables: { timeTrackingNumber: timeTracking.number, payload: createPayload(timeTracking) },
    resultField: 'updateTimeTracking',
  };
};

/** Request of a delete, chosen by the project number as `createTimeTrackingRequest`. */
const deleteTimeTrackingRequest = (timeTrackingNumber, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation deleteProjectTimeTracking($projectNumber: Int!, $timeTrackingNumber: Int!) {
          deleteProjectTimeTracking(
            projectNumber: $projectNumber
            timeTrackingNumber: $timeTrackingNumber
          )
        }
      `,
      variables: { projectNumber, timeTrackingNumber },
      resultField: 'deleteProjectTimeTracking',
    };
  }
  return {
    mutation: gql`
      mutation deleteTimeTracking($timeTrackingNumber: Int!) {
        deleteTimeTracking(timeTrackingNumber: $timeTrackingNumber)
      }
    `,
    variables: { timeTrackingNumber },
    resultField: 'deleteTimeTracking',
  };
};

// Actions
const actions = {
  /**
   * Loads a page of time trackings. With `projectNumber` the entries of the block
   * project-time-tracking, without it the global list (view time-tracking).
   */
  async fetchTimeTracking(
    { commit },
    { page, pageSize, sortField, sortOrder, filters, projectNumber }
  ) {
    const { query, pagesOf } = timeTrackingListRequest(projectNumber);
    try {
      const response = await apolloClient.query({
        query,
        variables: {
          ...(projectNumber ? { projectNumber } : {}),
          page,
          pageSize,
          sortField,
          sortOrder,
          filters,
        },
        fetchPolicy: 'network-only',
      });
      const timeTrackingPages = pagesOf(response.data);

      commit('SET_TIME_TRACKINGS', timeTrackingPages.timeTrackings);
      commit('SET_TIME_TRACKING_COUNT', timeTrackingPages.timeTrackingCount);
      commit('SET_TIME_TRACKING_TOTAL_HOURS', timeTrackingPages.timeTrackingTotalHours);
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * The appointments of the project for the appointment picker of the block
   * project-time-tracking. Commits nothing: the shared appointment list stays untouched.
   */
  async fetchProjectTimeTrackingAppointmentOptions(_, projectNumber) {
    const response = await apolloClient.query({
      query: PROJECT_TIME_TRACKING_APPOINTMENT_OPTIONS_QUERY,
      variables: { projectNumber },
      fetchPolicy: 'network-only',
    });
    return response.data.projectByNumber.blocks.timeTracking.appointmentOptions;
  },

  async stopTimeTracking({ commit }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation stopTimeTracking {
            stopTimeTracking {
              ...timeTrackingFields
            }
          }
          ${TIME_TRACKING_REQUEST_FIELDS}
        `,
      });

      const stopped = response.data.stopTimeTracking;
      if (stopped) {
        commit('ADD_OR_UPDATE_TIME_TRACKING', stopped);
      }
      return stopped;
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchTimeTrackingsByUser(
    { commit },
    { userId, page, pageSize, sortField, sortOrder, filters }
  ) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query timeTrackingsPaginatedByUser(
            $userId: ID!
            $page: Int!
            $pageSize: Int!
            $sortField: String
            $sortOrder: Int
            $filters: TimeTrackingFilterInput
          ) {
            timeTrackingsPaginatedByUser(
              userId: $userId
              page: $page
              pageSize: $pageSize
              sortField: $sortField
              sortOrder: $sortOrder
              filters: $filters
            ) {
              timeTrackingCount
              timeTrackingTotalHours
              timeTrackings {
                ...timeTrackingFields
              }
            }
          }
          ${TIME_TRACKING_REQUEST_FIELDS}
        `,
        variables: { userId, page, pageSize, sortField, sortOrder, filters },
        fetchPolicy: 'network-only',
      });

      commit('SET_TIME_TRACKINGS', response.data.timeTrackingsPaginatedByUser.timeTrackings);
      commit(
        'SET_TIME_TRACKING_COUNT',
        response.data.timeTrackingsPaginatedByUser.timeTrackingCount
      );
      commit(
        'SET_TIME_TRACKING_TOTAL_HOURS',
        response.data.timeTrackingsPaginatedByUser.timeTrackingTotalHours
      );
    } catch (err) {
      throw new Error(err);
    }
  },

  /**
   * start a new timeTracking for the current user
   * accepts optional project/appointment objects with id fields
   */
  async startTimeTracking({ commit }, { project, appointment, timeTrackingType } = {}) {
    try {
      const payload = {
        ...(project && project.id ? { projectId: project.id } : {}),
        ...(appointment && appointment.id ? { appointmentId: appointment.id } : {}),
        ...(timeTrackingType ? { timeTrackingType } : {}),
      };
      console.log('startTimeTracking() ', payload);
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation startTimeTracking($payload: TimeTrackingStartInput!) {
            startTimeTracking(timeTrackingStartInput: $payload) {
              ...timeTrackingFields
            }
          }
          ${TIME_TRACKING_REQUEST_FIELDS}
        `,
        variables: { payload },
      });

      const started = response.data.startTimeTracking;
      commit('ADD_TIME_TRACKING', started);
      return started;
    } catch (err) {
      throw new Error(err);
    }
  },

  /** Creates the entry; with `projectNumber` through the block project-time-tracking. */
  async createTimeTracking({ commit }, { timeTracking, projectNumber }) {
    const { mutation, variables, resultField } = createTimeTrackingRequest(
      timeTracking,
      projectNumber
    );

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      const createdTimeTracking = response.data[resultField];

      commit('ADD_TIME_TRACKING', createdTimeTracking);
      return createdTimeTracking.number;
    } catch (err) {
      throw new Error(err);
    }
  },

  /** Updates the entry; with `projectNumber` through the block project-time-tracking. */
  async updateTimeTracking({ commit, dispatch }, { timeTracking, projectNumber }) {
    const { mutation, variables, resultField } = updateTimeTrackingRequest(
      timeTracking,
      projectNumber
    );

    try {
      const response = await apolloClient.mutate({ mutation, variables });

      dispatch('message', {
        message: 'Zeiterfassung aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_TIME_TRACKING', response.data[resultField]);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Deletes the entry; with `projectNumber` through the block project-time-tracking. */
  async deleteTimeTracking({ commit, dispatch }, { timeTrackingNumber, projectNumber }) {
    const { mutation, variables, resultField } = deleteTimeTrackingRequest(
      timeTrackingNumber,
      projectNumber
    );

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      const isDeleted = response.data[resultField];

      if (isDeleted) {
        commit('REMOVE_TIME_TRACKING', timeTrackingNumber);
        dispatch('message', {
          message: 'Zeiterfassung gelöscht!',
          type: 'success',
          lifetime: 2,
        });
      }
      return isDeleted;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Löschen!',
        type: 'danger',
        lifetime: 2,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new timeTracking object in store and return it
   * @param {*} param
   * @param {*} timeTracking
   */
  async initTimeTracking({ commit }) {
    commit('ADD_OR_UPDATE_TIME_TRACKING', getDefaultTimeTracking());
  },
};

const mutations = {
  SET_TIME_TRACKINGS: (state, timeTrackings) => (state.timeTrackings = timeTrackings),
  ADD_TIME_TRACKING: (state, newTimeTracking) => state.timeTrackings.push(newTimeTracking),
  SET_TIME_TRACKING_COUNT: (state, count) => (state.timeTrackingCount = count),
  SET_TIME_TRACKING_TOTAL_HOURS: (state, totalHours) => (state.timeTrackingTotalHours = totalHours),
  ADD_OR_UPDATE_TIME_TRACKING: (state, newTimeTracking) => {
    const existingTimeTracking = state.timeTrackings.find(
      (timeTracking) => timeTracking.id === newTimeTracking.id
    );
    if (existingTimeTracking) {
      Object.assign(existingTimeTracking, newTimeTracking);
    } else {
      state.timeTrackings.push(newTimeTracking);
    }
  },
  REMOVE_TIME_TRACKING: (state, timeTrackingNumber) => {
    state.timeTrackings = state.timeTrackings.filter((tt) => tt.number !== timeTrackingNumber);
    state.timeTrackingCount = Math.max(0, state.timeTrackingCount - 1);
  },
};

// Getters
const getters = {
  getTimeTrackings: (state) => state.timeTrackings,
  getTimeTrackingCount: (state) => state.timeTrackingCount,
  gettimeTrackingTotalHours: (state) => state.timeTrackingTotalHours,
  getCurrentUser: (state) => state.currentUser,
  getTimeTrackingByNumber: (state) => (timeTrackingNumber) =>
    state.timeTrackings.find((timeTracking) => timeTracking.number == timeTrackingNumber),
};

export default {
  state,
  getters,
  actions,
  mutations,
};
